import { API_BASE_URL } from '../../config/serverApiConfig';
import axios from 'axios';
import errorHandler from '../../request/errorHandler';

/**
 * `/login` has three successful-looking 200 responses: a token, a TOTP challenge, or an email
 * challenge, so the caller has to read the body rather than the status. See `LoginResponse` in
 * `service/mfa.service.ts` for the shapes and the order they must be discriminated in.
 *
 * Errors propagate rather than going through `errorHandler`. The caller has to tell
 * `401 {message}` (bad credentials) from `503 {detail}` (a backing service is down: the email
 * factor fails closed rather than letting anyone in), and those two must not read alike.
 */
export const login = async ({
  email,
  password,
  otp
}: {
  email: string;
  password: string;
  otp?: string;
}) => {
  const payload: Record<string, any> = { email, password };
  if (otp) payload.otp = otp;
  const response = await axios.post(API_BASE_URL + 'r/v2/auth/login', payload);

  const { status, data } = response;

  return {
    status,
    data
  };
};

/**
 * Revoke the session server-side.
 *
 * The tokens are passed in rather than read out of storage, because the caller clears local
 * storage *first* — signing out must not depend on a network round trip to take effect on
 * this device — so by the time this runs there is nothing left to read.
 *
 * The refresh token goes in the `Authorization` header, which is this one route's contract:
 * it is the credential Keycloak's revocation endpoint takes, not the access token every other
 * route sends. The access token goes in the body so the API can evict it from its identity
 * cache; without it, the token being signed out keeps authenticating there for up to five
 * minutes after the session is destroyed.
 */
export const logout = async (
  tokens: { refreshToken?: string; accessToken?: string } = {}
) => {
  const { refreshToken, accessToken } = tokens;
  if (!refreshToken) {
    // Nothing to revoke. Not an error: the local session is already gone by this point, and
    // this is also the path taken on boot when there was never a session to begin with.
    console.warn('No refresh token to revoke; local session already cleared.');
    return;
  }

  // Errors propagate. A non-2xx here means the session is still live at Keycloak, which the
  // caller logs — it does not mean the sign-out failed, because the local half already ran.
  await axios.delete(API_BASE_URL + 'r/v2/auth/logout', {
    headers: {
      Authorization: `Bearer ${refreshToken}`
    },
    data: accessToken ? { access_token: accessToken } : {}
  });
};

export const getMe = async (workspaceId?: string) => {
  try {
    const user = sessionStorage.getItem('auth');
    const tmpAuth = user ? JSON.parse(user).current : null;
    const url = workspaceId
      ? API_BASE_URL + 'r/v2/users/me/details?workspace_id=' + workspaceId
      : API_BASE_URL + 'r/v2/users/me/details';
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${tmpAuth.access_token}`
      }
    });
    if (!response || !response.data) {
      throw new Error('Get me API response is invalid.');
    }
    return {
      data: response.data,
      status: 200
    };
  } catch (error) {
    return errorHandler(error);
  }
};